'use client';

import {
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  type SxProps,
  type Theme,
  type FabProps,
} from '@mui/material';
import { useMemo, useState } from 'react';

type Direction = 'up' | 'down' | 'left' | 'right';
type TriggerMode = 'hover' | 'click' | 'both';

export type SpeedDialMenuAction = {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tooltipOpen?: boolean;
  tooltipTitle?: React.ReactNode;
  hideTooltip?: boolean;
};

type SpeedDialMenuProps = {
  ariaLabel: string;
  actions: SpeedDialMenuAction[];
  direction?: Direction;
  triggerMode?: TriggerMode;
  persistActionTooltips?: boolean;
  closeOnActionSelect?: boolean;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  speedDialSx?: SxProps<Theme>;
  fabProps?: Partial<FabProps>;
};

export function SpeedDialMenu({
  ariaLabel,
  actions,
  direction = 'right',
  triggerMode = 'both',
  persistActionTooltips = false,
  closeOnActionSelect = true,
  trigger,
  open,
  onOpen,
  onClose,
  speedDialSx,
  fabProps,
}: SpeedDialMenuProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof open === 'boolean';
  const resolvedOpen = isControlled ? open : internalOpen;

  const resolvedActions = useMemo(
    () =>
      actions.map((action) => ({
        ...action,
        tooltipOpen: action.tooltipOpen ?? persistActionTooltips,
      })),
    [actions, persistActionTooltips],
  );

  const handleOpen = (_event?: unknown, reason?: string) => {
    const allowOpen =
      triggerMode === 'both' ||
      (triggerMode === 'click' && reason === 'toggle') ||
      (triggerMode === 'hover' &&
        (reason === 'mouseEnter' || reason === 'focus'));

    if (!allowOpen) {
      return;
    }

    if (!isControlled) {
      setInternalOpen(true);
    }
    onOpen?.();
  };

  const handleClose = (_event?: unknown, reason?: string) => {
    const allowClose =
      triggerMode === 'both' ||
      (triggerMode === 'click' &&
        (reason === 'toggle' ||
          reason === 'blur' ||
          reason === 'escapeKeyDown')) ||
      (triggerMode === 'hover' &&
        (reason === 'toggle' ||
          reason === 'blur' ||
          reason === 'escapeKeyDown' ||
          reason === 'mouseLeave'));

    if (!allowClose) {
      return;
    }

    if (!isControlled) {
      setInternalOpen(false);
    }
    onClose?.();
  };

  return (
    <SpeedDial
      ariaLabel={ariaLabel}
      direction={direction}
      icon={trigger ?? <SpeedDialIcon />}
      open={resolvedOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      FabProps={fabProps}
      sx={speedDialSx}
    >
      {resolvedActions.map((action) => (
        <SpeedDialAction
          key={action.key}
          icon={action.icon}
          tooltipTitle={
            action.hideTooltip ? '' : (action.tooltipTitle ?? action.label)
          }
          tooltipOpen={action.hideTooltip ? false : action.tooltipOpen}
          FabProps={{
            disabled: action.disabled,
            'aria-label': action.label,
            tabIndex: resolvedOpen ? 0 : -1,
          }}
          onClick={() => {
            action.onClick();
            if (closeOnActionSelect) {
              handleClose(undefined, 'toggle');
            }
          }}
        />
      ))}
    </SpeedDial>
  );
}
